import type { BrowserUpdate, ScreenSlot } from './velis-perception'

import { boolean, object, optional, picklist, record, safeParse, string, unknown } from 'valibot'

/** Same dedupe key stays quiet for this long after a decision. */
export const attentionCooldownMs = 60_000

export const attentionActions = ['IGNORE', 'REMEMBER', 'NOTIFY', 'SPEAK', 'TOOL'] as const

export type AttentionAction = typeof attentionActions[number]

export const attentionSources = ['reminder', 'browser', 'desktop', 'task', 'plugin', 'screen'] as const

export type AttentionSource = typeof attentionSources[number]

export const attentionSpeakConditions = ['deadline', 'task-changed', 'user-available'] as const

export type AttentionSpeakCondition = typeof attentionSpeakConditions[number]

export const attentionToolEffects = ['read', 'low-risk-write', 'side-effect'] as const

export type AttentionToolEffect = typeof attentionToolEffects[number]

export interface AttentionToolRequest {
  name: string
  effect: AttentionToolEffect
  user_intent?: boolean
  preauthorized?: boolean
}

/**
 * One external event that can enter the attention policy.
 * Browser and screen updates reuse the existing context slots. They do not open a second bus.
 */
export interface AttentionEvent {
  event_id: string
  source: AttentionSource
  type: string
  occurred_at: number
  summary?: string
  payload?: Record<string, unknown>
  dedupe_key: string
  urgency?: 'immediate' | 'soon' | 'later'
  speak_condition?: AttentionSpeakCondition
  tool?: AttentionToolRequest
}

export type AttentionContextSlot = 'velis:browser' | 'velis:screen' | 'memory' | 'none'

export interface AttentionContextRef {
  slot: AttentionContextSlot
  summary?: string
}

export interface AttentionDecision {
  event: AttentionEvent
  contexts: AttentionContextRef[]
  action: AttentionAction
  reason: string
  deduped: boolean
  suppressed: boolean
  final_action: AttentionAction
  tool_executed: boolean
}

export interface AttentionHistoryEntry {
  dedupe_key: string
  action: AttentionAction
  at: number
  speak_condition?: AttentionSpeakCondition
}

export const attentionBadcaseKinds = [
  'useful-proactive',
  'unnecessary-interruption',
  'missed-important-event',
  'repeated-notification',
] as const

export type AttentionBadcaseKind = typeof attentionBadcaseKinds[number]

export interface AttentionBadcase {
  kind: AttentionBadcaseKind
  event_id: string
  note: string
  at: number
}

const sourceSchema = picklist(attentionSources)
const urgencySchema = picklist(['immediate', 'soon', 'later'])
const speakConditionSchema = picklist(attentionSpeakConditions)
const toolSchema = object({
  name: string(),
  effect: picklist(attentionToolEffects),
  user_intent: optional(boolean()),
  preauthorized: optional(boolean()),
})
const sparkInputSchema = object({
  id: string(),
  eventId: string(),
  kind: string(),
  urgency: optional(urgencySchema),
  headline: string(),
  note: optional(string()),
  payload: optional(record(string(), unknown())),
  metadata: optional(record(string(), unknown())),
})

function textField(recordValue: Record<string, unknown> | undefined, key: string) {
  const value = recordValue?.[key]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function parsedSource(value: string | undefined): AttentionSource | undefined {
  if (!value)
    return undefined
  const parsed = safeParse(sourceSchema, value)
  return parsed.success ? parsed.output : undefined
}

function parsedSpeakCondition(value: unknown): AttentionSpeakCondition | undefined {
  const parsed = safeParse(speakConditionSchema, value)
  return parsed.success ? parsed.output : undefined
}

function fallbackSource(pageNotify: boolean, kind: string): AttentionSource {
  if (pageNotify)
    return 'browser'
  if (kind === 'reminder')
    return 'reminder'
  return 'plugin'
}

function fallbackDedupeKey(source: AttentionSource, kind: string, headline: string, pageUrl: string | undefined) {
  if (source === 'browser' && pageUrl)
    return `browser:${pageUrl}`
  return `${source}:${kind}:${headline}`
}

function parsedTool(value: unknown): AttentionToolRequest | undefined {
  const parsed = safeParse(toolSchema, value)
  if (!parsed.success || !parsed.output.name.trim())
    return undefined
  return {
    name: parsed.output.name.trim(),
    effect: parsed.output.effect,
    ...(parsed.output.user_intent ? { user_intent: true } : {}),
    ...(parsed.output.preauthorized ? { preauthorized: true } : {}),
  }
}

/**
 * Reads one `spark:notify` into an attention event.
 * A notify without a source or `dedupe_key` stays on the existing character path.
 *
 * @example
 * attentionEventFromSparkNotify({
 *   id: 'id-2',
 *   eventId: 'event-2',
 *   kind: 'ping',
 *   urgency: 'soon',
 *   headline: 'Notes changed',
 *   payload: { source: 'browser', dedupe_key: 'browser:https://example.test/notes' },
 * }, 1000)
 * // => { event_id: 'event-2', source: 'browser', dedupe_key: 'browser:https://example.test/notes', ... }
 */
export function attentionEventFromSparkNotify(input: {
  id: string
  eventId: string
  kind: string
  urgency?: 'immediate' | 'soon' | 'later'
  headline: string
  note?: string
  payload?: Record<string, unknown>
  metadata?: Record<string, unknown>
}, occurredAt: number): AttentionEvent | undefined {
  const parsed = safeParse(sparkInputSchema, input)
  if (!parsed.success)
    return undefined

  const payload = parsed.output.payload
  const metadata = parsed.output.metadata
  const source = parsedSource(textField(payload, 'source') ?? textField(metadata, 'source'))
  const dedupeKey = textField(payload, 'dedupe_key') ?? textField(metadata, 'dedupe_key')
  const pageUrl = textField(payload, 'url')
  const pageNotify = !source && Boolean(pageUrl)
  if (!source && !dedupeKey && !pageNotify)
    return undefined

  const resolvedSource = source ?? fallbackSource(pageNotify, parsed.output.kind)
  const speakCondition = parsedSpeakCondition(payload?.speak_condition)
  const tool = parsedTool(payload?.tool)

  return {
    event_id: parsed.output.eventId,
    source: resolvedSource,
    type: parsed.output.kind,
    occurred_at: occurredAt,
    summary: parsed.output.headline,
    ...(payload ? { payload } : {}),
    dedupe_key: dedupeKey ?? fallbackDedupeKey(resolvedSource, parsed.output.kind, parsed.output.headline, pageUrl),
    ...(parsed.output.urgency ? { urgency: parsed.output.urgency } : {}),
    ...(speakCondition ? { speak_condition: speakCondition } : {}),
    ...(tool ? { tool } : {}),
  }
}

/**
 * Reads one browser context update into an attention event.
 * The dedupe key is the page URL, so title and subtitle ticks share one cooldown.
 *
 * @example
 * attentionEventFromBrowserUpdate({
 *   lane: 'web:page',
 *   source: 'web-extension',
 *   capturedAt: 1000,
 *   url: 'https://example.test/notes',
 *   text: 'Notes',
 * })
 * // => { source: 'browser', dedupe_key: 'browser:https://example.test/notes', ... }
 */
export function attentionEventFromBrowserUpdate(update: BrowserUpdate): AttentionEvent {
  const identity = update.url ?? update.sourceRef ?? update.lane
  return {
    event_id: `browser:${update.capturedAt}:${identity}`,
    source: 'browser',
    type: update.lane,
    occurred_at: update.capturedAt,
    summary: update.text.trim() || update.title,
    payload: {
      lane: update.lane,
      ...(update.url ? { url: update.url } : {}),
      ...(update.title ? { title: update.title } : {}),
    },
    dedupe_key: `browser:${identity}`,
  }
}

/**
 * Reads one stored screen slot into an attention event.
 *
 * @example
 * attentionEventFromScreenSlot({
 *   kind: 'screen',
 *   source: 'screen-capture',
 *   sourceRef: 'screen:1',
 *   capturedAt: 1000,
 *   summary: 'A code editor is open.',
 * })
 * // => { source: 'screen', dedupe_key: 'screen:screen:1', ... }
 */
export function attentionEventFromScreenSlot(slot: ScreenSlot): AttentionEvent {
  const identity = slot.sourceRef ?? slot.source
  return {
    event_id: `screen:${slot.capturedAt}:${identity}`,
    source: 'screen',
    type: 'screen-summary',
    occurred_at: slot.capturedAt,
    summary: slot.summary,
    dedupe_key: `screen:${identity}`,
  }
}

function lastHistory(history: readonly AttentionHistoryEntry[], dedupeKey: string) {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = history[index]
    if (entry?.dedupe_key === dedupeKey)
      return entry
  }
  return undefined
}

function baseDecision(event: AttentionEvent): { action: AttentionAction, reason: string } {
  const tool = event.tool
  if (tool?.effect === 'side-effect')
    return { action: 'NOTIFY', reason: 'side-effect-blocked' }
  if (tool?.effect === 'low-risk-write') {
    return {
      action: 'NOTIFY',
      reason: tool.user_intent || tool.preauthorized ? 'write-authorized-not-run' : 'write-needs-authorization',
    }
  }
  if (tool?.effect === 'read' && event.source !== 'browser' && event.source !== 'screen')
    return { action: 'TOOL', reason: 'read-for-judgment' }
  if (event.source === 'browser' || event.source === 'screen')
    return { action: 'REMEMBER', reason: 'context-held' }
  if ((event.source === 'reminder' || event.source === 'task') && event.urgency === 'later')
    return { action: 'REMEMBER', reason: 'not-urgent' }
  if (event.source === 'reminder' || event.source === 'task' || event.urgency === 'immediate' || event.urgency === 'soon') {
    return {
      action: 'NOTIFY',
      reason: event.source === 'reminder' || event.source === 'task' ? 'reminder' : 'immediate',
    }
  }
  return { action: 'IGNORE', reason: 'not-relevant' }
}

/**
 * Chooses one attention action from one event and the decisions already made for its dedupe key.
 * The choice is a rule. It does not call a model, and it does not schedule a later scan.
 *
 * A browser or screen update stays on REMEMBER until a later event brings a new speak condition.
 * A side-effect tool and a low-risk write are not run. A read tool runs only for that judgment.
 */
export function decideAttention(input: {
  event: AttentionEvent
  now: number
  history: readonly AttentionHistoryEntry[]
  contexts?: readonly AttentionContextRef[]
  cooldownMs?: number
  executeTool?: (request: AttentionToolRequest) => void
}): { decision: AttentionDecision, history: AttentionHistoryEntry[] } {
  const cooldownMs = input.cooldownMs ?? attentionCooldownMs
  const previous = lastHistory(input.history, input.event.dedupe_key)
  let { action, reason } = baseDecision(input.event)

  const canRaise = action === 'REMEMBER' || action === 'NOTIFY'
  const raisedBefore = previous?.action === 'REMEMBER' || previous?.action === 'NOTIFY'
  if (canRaise && !input.event.tool && input.event.speak_condition && previous && raisedBefore && previous.speak_condition !== input.event.speak_condition) {
    action = 'SPEAK'
    reason = 'speak-condition'
  }

  const withinCooldown = previous !== undefined && input.now - previous.at < cooldownMs
  const sameAction = previous?.action === action
  const sameSpeakCondition = input.event.speak_condition !== undefined && previous?.speak_condition === input.event.speak_condition
  const deduped = withinCooldown && (sameAction || sameSpeakCondition) && action !== 'IGNORE'
  const finalAction: AttentionAction = deduped ? 'IGNORE' : action
  let toolExecuted = false
  if (!deduped && action === 'TOOL' && input.event.tool && input.executeTool) {
    input.executeTool(input.event.tool)
    toolExecuted = true
  }

  const contexts = input.contexts && input.contexts.length > 0
    ? [...input.contexts]
    : [{ slot: 'none' as const }]

  return {
    decision: {
      event: input.event,
      contexts,
      action,
      reason: deduped ? 'deduped' : reason,
      deduped,
      suppressed: deduped,
      final_action: finalAction,
      tool_executed: toolExecuted,
    },
    history: [...input.history, {
      dedupe_key: input.event.dedupe_key,
      action,
      at: input.now,
      ...(input.event.speak_condition ? { speak_condition: input.event.speak_condition } : {}),
    }],
  }
}

/**
 * Formats one decision as a single developer line.
 * The line names the event, the context slot, the action, the reason, and dedupe or suppression.
 *
 * @example
 * formatAttentionTrace(runRepeatableAttentionExample(0).first)
 * // => 'example-reminder-1 · reminder · NOTIFY · reminder · memory Project is Velis · clear'
 */
export function formatAttentionTrace(decision: AttentionDecision) {
  const context = decision.contexts.map((item) => {
    if (item.slot === 'none')
      return 'none'
    return item.summary ? `${item.slot} ${item.summary}` : item.slot
  }).join(', ')
  const flags = [
    decision.deduped ? 'deduped' : undefined,
    decision.suppressed ? 'suppressed' : undefined,
  ].filter((flag): flag is string => Boolean(flag))

  return [
    decision.event.event_id,
    decision.event.source,
    decision.final_action,
    decision.reason,
    context || 'none',
    flags.length > 0 ? flags.join(', ') : 'clear',
  ].join(' · ')
}

/** Four badcase slots. This phase fills a slot only from a recorded example or a suppressed repeat. */
export function emptyAttentionBadcases(): Record<AttentionBadcaseKind, AttentionBadcase[]> {
  return {
    'useful-proactive': [],
    'unnecessary-interruption': [],
    'missed-important-event': [],
    'repeated-notification': [],
  }
}

/**
 * Runs the reminder chain twice.
 * The first decision notifies. The second decision for the same dedupe key is suppressed.
 */
export function runRepeatableAttentionExample(now = 0): {
  first: AttentionDecision
  repeat: AttentionDecision
  badcases: Record<AttentionBadcaseKind, AttentionBadcase[]>
} {
  const event: AttentionEvent = {
    event_id: 'example-reminder-1',
    source: 'reminder',
    type: 'reminder',
    occurred_at: now,
    summary: 'Project check-in is due',
    dedupe_key: 'reminder:example-project-check-in',
    urgency: 'soon',
  }
  const contexts: AttentionContextRef[] = [{ slot: 'memory', summary: 'Project is Velis' }]
  const firstPass = decideAttention({ event, contexts, now, history: [] })
  const repeatPass = decideAttention({
    event: { ...event, event_id: 'example-reminder-2', occurred_at: now + 1_000 },
    contexts,
    now: now + 1_000,
    history: firstPass.history,
  })
  const badcases = emptyAttentionBadcases()
  badcases['repeated-notification'].push({
    kind: 'repeated-notification',
    event_id: repeatPass.decision.event.event_id,
    note: 'The same dedupe key was suppressed during cooldown.',
    at: now + 1_000,
  })
  return {
    first: firstPass.decision,
    repeat: repeatPass.decision,
    badcases,
  }
}
